type TransformContext = {
  input: string
  words: string[]
}

type TransformStep = (ctx: TransformContext) => TransformContext
type Formatter = (ctx: TransformContext) => string

const WORD_SPLIT_REGEX =
  /[^a-zA-Z0-9]+|(?<=[a-z])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])|(?<=[a-zA-Z])(?=[0-9])|(?<=[0-9])(?=[a-zA-Z])/

const tokenize: TransformStep = (ctx) => ({
  ...ctx,
  words: ctx.input.split(WORD_SPLIT_REGEX).filter(Boolean),
})

const normalizeLower: TransformStep = (ctx) => ({
  ...ctx,
  words: ctx.words.map((w) => w.toLowerCase()),
})

const normalizeUpper: TransformStep = (ctx) => ({
  ...ctx,
  words: ctx.words.map((w) => w.toUpperCase()),
})

const capitalizeWords: TransformStep = (ctx) => ({
  ...ctx,
  words: ctx.words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()),
})

const join =
  (separator: string): Formatter =>
  (ctx) =>
    ctx.words.join(separator)

const camelFormatter: Formatter = (ctx) =>
  ctx.words.length === 0
    ? ""
    : ctx.words[0]!.toLowerCase() +
      ctx.words
        .slice(1)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join("")

export type CaseType =
  | "lowercase"
  | "uppercase"
  | "sentence"
  | "title"
  | "snake"
  | "kebab"
  | "camel"
  | "pascal"
  | "dot"
  | "constant"

type CaseDefinition = {
  steps: TransformStep[]
  format: Formatter
}

const cases: Record<CaseType, CaseDefinition> = {
  lowercase: {
    steps: [tokenize, normalizeLower],
    format: join(" "),
  },

  uppercase: {
    steps: [tokenize, normalizeUpper],
    format: join(" "),
  },

  sentence: {
    steps: [tokenize, normalizeLower],
    format: (ctx) => {
      const s = ctx.words.join(" ")
      return s.charAt(0).toUpperCase() + s.slice(1)
    },
  },

  title: {
    steps: [tokenize, capitalizeWords],
    format: join(" "),
  },

  snake: {
    steps: [tokenize, normalizeLower],
    format: join("_"),
  },

  kebab: {
    steps: [tokenize, normalizeLower],
    format: join("-"),
  },

  dot: {
    steps: [tokenize, normalizeLower],
    format: join("."),
  },

  constant: {
    steps: [tokenize, normalizeUpper],
    format: join("_"),
  },

  pascal: {
    steps: [tokenize, capitalizeWords],
    format: join(""),
  },

  camel: {
    steps: [tokenize],
    format: camelFormatter,
  },
}

export function toCase(input: string, type: CaseType): string {
  if (typeof input !== "string" || !input.trim()) {
    return ""
  }

  const def = cases[type]

  if (!def) {
    throw new Error(`Unsupported case type: ${type}`)
  }

  let ctx: TransformContext = {
    input,
    words: [],
  }

  for (const step of def.steps) {
    ctx = step(ctx)
  }

  return def.format(ctx)
}

export function extendCases(custom: Record<string, CaseDefinition>) {
  Object.assign(cases, custom)
}
